class ParentProductGroup extends HTMLElement {
    constructor() {
        super();
        this.form = this.querySelector('[is="parent-product-form"]');
        if (!this.form) return;

        this.setupQuantityListeners();
        this.setupRemoveItemListeners();
        this.setupAccordionListeners();

        requestAnimationFrame(() => this.syncNotifyMeWidths());
        this._syncOnResize = () => {
            clearTimeout(this._resizeTimer);
            this._resizeTimer = setTimeout(() => this.syncNotifyMeWidths(), 100);
        };
        window.addEventListener('resize', this._syncOnResize);
    }

    disconnectedCallback() {
        if (this._syncOnResize) window.removeEventListener('resize', this._syncOnResize);
    }

    syncNotifyMeWidths() {
        let width = 0;
        for (const qty of this.querySelectorAll('.pgpc-variant-actions .quantity')) {
            if (qty.offsetWidth > 0) { width = qty.offsetWidth; break; }
        }
        if (width === 0) return;
        this.querySelectorAll('.bis-btn-parent-group').forEach(btn => {
            btn.style.setProperty('width', width + 'px', 'important');
        });
    }

    setupQuantityListeners() {
        const quantityInputs = this.querySelectorAll('.quantity__input[data-variant-id]');
        quantityInputs.forEach(input => {
            input.addEventListener('change', () => {
                this.updateCartState();
                this.publishChildProductSelected(input);
            });
            input.addEventListener('input', () => this.updateCartState());
        });

        const quantityButtons = this.querySelectorAll('.quantity__button');
        quantityButtons.forEach(button => {
            button.addEventListener('click', () => {
                setTimeout(() => this.updateCartState(), 100);
            });
        });

        // Initial state update
        this.updateCartState();
    }

    publishChildProductSelected(input) {
        const quantity = parseInt(input.value) || 0;
        if (quantity === 0) return;
        if (typeof Shopify === 'undefined' || !Shopify.analytics) {
            return;
        }
        const payload = {
            parent_product: {
                product_title: this.dataset.parentProductTitle,
                product_id: this.dataset.parentProductId
            },
            child_product: {
                variant_id: input.dataset.variantId,
                product_title: input.dataset.title,
                price: parseFloat(input.dataset.price) / 100,
                quantity: quantity,
                currency: Shopify.currency?.active
            }
        };
        Shopify.analytics.publish('child_product_selected', payload);
    }

    setupRemoveItemListeners() {
        const summaryBox = this.querySelector('.selected-items-list');
        if (!summaryBox) return;
        summaryBox.addEventListener('click', (event) => {
            const removeButton = event.target.closest('.remove-item');
            if (removeButton) {
                const variantId = removeButton.dataset.variantId;
                const input = this.querySelector(`.quantity__input[data-variant-id="${variantId}"]`);
                if (input) {
                    input.value = 0;
                    this.updateCartState();
                }
            }
        });
    }

    updateCartState() {
        // If element is no longer connected, skip updates to avoid errors
        if (!this.isConnected) return;

        const items = this.getSelectedItems();
        const addToCartButton = this.querySelector('[data-parent-add-to-cart]');
        const summaryBox = this.querySelector('.selected-items-summary');
        const summaryList = this.querySelector('.selected-items-list');
        const totalRow = this.querySelector('.parent-total-row');
        const totalSaleEl = this.querySelector('.parent-total-sale');
        const totalCompareEl = this.querySelector('.parent-total-compare-at');

        let totalPrice = 0;
        let totalCompareAt = 0;
        items.forEach(item => {
            totalPrice += item.price * item.quantity;
            totalCompareAt += (item.compareAtPrice > item.price ? item.compareAtPrice : item.price) * item.quantity;
        });

        // Update accordion header selection state
        this.querySelectorAll('details.parent-product-group-inner').forEach(accordion => {
            const inputs = accordion.querySelectorAll('.quantity__input[data-variant-id]');
            let count = 0;
            inputs.forEach(input => {
                count += parseInt(input.value) || 0;
            });
            const countEl = accordion.querySelector('.accordion-selection-count');
            if (count > 0) {
                if (countEl) countEl.textContent = '(' + count + ' Selected)';
                // Only show green state once the accordion is closed
                if (!accordion.open) {
                    accordion.classList.add('has-selection');
                } else {
                    accordion.classList.remove('has-selection');
                }
            } else {
                accordion.classList.remove('has-selection');
                if (countEl) countEl.textContent = '';
            }
        });

        // Update total row
        if (totalRow) {
            if (items.length > 0) {
                totalRow.hidden = false;
                if (totalSaleEl) totalSaleEl.textContent = this.formatMoney(totalPrice);
                if (totalCompareEl) {
                    totalCompareEl.textContent = totalCompareAt > totalPrice
                        ? this.formatMoney(totalCompareAt)
                        : '';
                }
            } else {
                totalRow.hidden = true;
            }
        }

        // Update button state (keep enabled so we can show error + scroll when no items)
        if (addToCartButton) {
            addToCartButton.disabled = false;
            const buttonTextSpan = addToCartButton.querySelector('.btn-text > span');
            if (buttonTextSpan) {
                buttonTextSpan.textContent = items.length === 0 ? 'Please select items' : 'Add to Cart';
            }
        }


        // Hide "no items selected" error when user has selected items
        const groupError = this.querySelector('.parent-product-group__error');
        if (groupError && items.length > 0) {
            groupError.hidden = true;
            groupError.textContent = '';
        }

        // Update summary box
        if (summaryBox && summaryList) {
            if (items.length > 0) {
                summaryBox.style.display = 'block';
                summaryList.innerHTML = items.map(item => `
                    <div class="selected-items-list-item">
                        <span class="item-title">${this.escapeHTML(item.title)} (${item.quantity})</span>
                        <button type="button" class="remove-item" data-variant-id="${item.id}" aria-label="Remove ${this.escapeHTML(item.title)}">
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 2L2 8M2 2L8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                `).join('');
            } else {
                summaryBox.style.display = 'none';
                summaryList.innerHTML = '';
            }
        }
    }

    getSelectedItems() {
        const items = [];
        const quantityInputs = this.querySelectorAll('.quantity__input[data-variant-id]');
        
        quantityInputs.forEach(input => {
            const quantity = parseInt(input.value) || 0;
            if (quantity > 0) {
                items.push({
                    id: parseInt(input.dataset.variantId),
                    quantity: quantity,
                    price: parseFloat(input.dataset.price),
                    compareAtPrice: parseFloat(input.dataset.compareAtPrice) || 0,
                    title: input.dataset.title || 'Product'
                });
            }
        });

        return items;
    }

    escapeHTML(text) {
        const map = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
        };
        return String(text).replace(/[&<>"']/g, (m) => map[m]);
    }

    formatMoney(cents) {
        const currencyEnabled = window.shopSettings?.currencyEnabled;
        const moneyWithCurrencyFormat = window.shopSettings?.moneyWithCurrencyFormat;
        const moneyFormat = window.shopSettings?.moneyFormat;
        const formatString = currencyEnabled ? moneyWithCurrencyFormat : moneyFormat;
        const amount = (cents / 100).toFixed(2);
        return formatString ? formatString.replace(/\{\{\s*amount\s*\}\}/, amount) : amount;
    }

    async handleSubmit(event) {
        event.preventDefault();
        const items = this.getSelectedItems().map(item => ({
            id: item.id,
            quantity: item.quantity
        }));

        if (items.length === 0) {
            this.showNoItemsError();
            return;
        }

        if (typeof Shopify !== 'undefined' && Shopify.analytics) {
            const fullItems = this.getSelectedItems();
            const atcPayload = {
                source: 'parent_pdp',
                items: fullItems.map(item => ({
                    variant_id: String(item.id),
                    product_title: item.title,
                    price: item.price / 100,
                    quantity: item.quantity,
                    currency: Shopify.currency?.active
                }))
            };
            Shopify.analytics.publish('atc_with_source', atcPayload);
        } else {
        }

        const submitButton = this.querySelector('[data-parent-add-to-cart]');
        const errorMessage = this.querySelector('.product-form__error-message');

        try {
            const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ items })
            });

            if (!response.ok) throw new Error('Network response was not ok');

            // Reset quantities
            this.querySelectorAll('.quantity__input[data-variant-id]').forEach(input => {
                input.value = 0;
            });
            this.updateCartState();

            // Close the quickview modal
            const quickView = document.querySelector('quick-view[open]');
            if (quickView && typeof quickView.hide === 'function') {
                quickView.hide();
            }

            // Get the cart drawer
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
                // Trigger cart refresh and open drawer
                cartDrawer.show();
                document.dispatchEvent(new CustomEvent('cart:refresh', {
                    bubbles: true
                }));
            }

            errorMessage.hidden = true;

        } catch (error) {
            console.error('Error:', error);
            errorMessage.textContent = error.message || 'Unable to add items to cart. Please try again.';
            errorMessage.hidden = false;
            submitButton.removeAttribute('aria-busy');
            submitButton.disabled = false;
        }
    }

    showNoItemsError() {
        const errorEl = this.querySelector('.parent-product-group__error');

        if (errorEl) {
            errorEl.textContent = this.dataset.noItemsError || 'Oops! Please select a product before adding to basket.';
            errorEl.hidden = false;
            errorEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    setupAccordionListeners() {
        const details = this.querySelectorAll('details.parent-product-group-inner');
        
        // Close any accordions with data-auto-close attribute by default,
        // unless the section setting has all accordions expanded.
        if (!this.hasAttribute('data-expand-all')) {
            details.forEach(detail => {
                if (detail.hasAttribute('data-auto-close')) {
                    detail.open = false;
                }
            });
        }

        details.forEach(detail => {
            detail.addEventListener('toggle', (event) => {
                if (detail.open && !this.hasAttribute('data-expand-all')) {
                    // Close all other accordions (only in single-open mode)
                    details.forEach(otherDetail => {
                        if (otherDetail !== detail && otherDetail.open) {
                            otherDetail.open = false;
                        }
                    });
                }
                // Re-evaluate green state whenever any accordion opens or closes
                this.updateCartState();
                // Re-sync notify-me widths now that quantities may be visible
                if (detail.open) this.syncNotifyMeWidths();
            });
        });
    }
}

// Register the custom elements
customElements.define('parent-product-group', ParentProductGroup);

// Also register the form element since we still need it
class ParentProductForm extends HTMLFormElement {
    constructor() {
        super();
        this.addEventListener('submit', (event) => {
            event.preventDefault();
            const parentGroup = this.closest('parent-product-group');
            if (parentGroup) {
                parentGroup.handleSubmit(event);
            }
        });
    }
}

customElements.define('parent-product-form', ParentProductForm, { extends: 'form' });